import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import sessionUtils from '../../data/session-utils'

import useCheckoutContext from '../../data/useCheckoutContext'
import useInterval from '../../data/useInterval'
import useMutationWithErrorHandling from '../../data/useMutationWithErrorHandling'
import useOrder from '../../data/useOrder'
import { BlockingError } from '../../lib/custom-errors'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { useIntl } from '../../localization/react'
import { OrderState } from '../../typings/Order'

import {
  PaymentTimerAbortOrderMutation,
  PaymentTimerAbortOrderMutationVariables,
  PaymentTimerOrderQuery,
  PaymentTimerAbortOrderDocument,
  PaymentTimerOrderDocument,
} from '../../graphql'
import PaymentTimerComponent from './PaymentTimerComponent'

const PaymentTimer: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation()
  const intl = useIntl()
  const context = useCheckoutContext()

  const { orderId } = getPhoenixUrlParams(location.search)
  const [timeRemainingInMilliseconds, setTimeRemainingInMilliseconds] = useState(0)
  const [wasTimeoutReached, setWasTimeoutReached] = useState(false)

  const { orderData, isOrderLoading } = useOrder<PaymentTimerOrderQuery>(PaymentTimerOrderDocument)

  const [abortOrder] = useMutationWithErrorHandling<
    PaymentTimerAbortOrderMutation,
    PaymentTimerAbortOrderMutationVariables
  >(PaymentTimerAbortOrderDocument, { variables: { orderId: orderId as string } })

  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault()
    event.returnValue = ''
  }, [])

  const handlePopState = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    abortOrder()
    window.removeEventListener('popstate', handlePopState)
  }, [])

  // Is both responsible for setting the session storage item and for trying
  // to abort the order directly if it can
  const handleUnload = useCallback(() => {
    sessionUtils.setAbortedOrderId(orderId!)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    abortOrder()
  }, [])

  const handleTimeout = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    abortOrder()
    setWasTimeoutReached(true)
  }, [])

  useInterval(() => {
    if (Math.floor(timeRemainingInMilliseconds / 1000) > 0) {
      setTimeRemainingInMilliseconds(timeRemainingInMilliseconds - 1000)
    }
  }, 1000)

  useEffect(() => {
    if (!wasTimeoutReached) return

    throw new BlockingError('The payment timer has timed out', intl.orderTimeoutError())
  }, [wasTimeoutReached])

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('unload', handleUnload)
    // We only add the event listener, but don't remove it, because we actually
    // need for this to exist on the window if the component gets unmounted
    // by going back to the previous page. It will be removed tho if the payment
    // is processing or the order state is confirmed
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('unload', handleUnload)
    }
  }, [])

  useEffect(() => {
    // Because a payment can fail, we can end up removing the listeners, and
    // never add them again, which would then cause the order not to be aborted
    // if the user does any unload action after the failed payment, so we re-add
    // the listeners in this case. Theres no drawback to it.
    if (!context.isPaymentProcessing) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('unload', handleUnload)
      window.addEventListener('popstate', handlePopState)
      return
    }

    window.removeEventListener('beforeunload', handleBeforeUnload)
    window.removeEventListener('unload', handleUnload)
    window.removeEventListener('popstate', handlePopState)
  }, [context.isPaymentProcessing])

  useEffect(() => {
    if (!orderData || (orderData && orderData.order.state !== OrderState.ORDER_STATE_CONFIRMED)) return

    window.removeEventListener('beforeunload', handleBeforeUnload)
    window.removeEventListener('unload', handleUnload)
    window.removeEventListener('popstate', handlePopState)
  }, [orderData])

  useEffect(() => {
    if (!orderData || !orderData.order.confirmableUntil) return

    const expirationDate = new Date(orderData.order.confirmableUntil)

    const timeRemaining = Math.max(expirationDate.getTime() - Date.now(), 0)
    const timeoutInterval = setTimeout(handleTimeout, timeRemaining)

    setTimeRemainingInMilliseconds(timeRemaining)

    return () => {
      clearInterval(timeoutInterval)
    }
  }, [orderData])

  if (!orderData || isOrderLoading || !timeRemainingInMilliseconds) {
    return null
  }

  return <PaymentTimerComponent timeRemainingInMilliseconds={timeRemainingInMilliseconds} />
}

export default PaymentTimer
